import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { AnalyticsService, SessionData } from '@shared/services/analytics.service';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { AssessmentCriteria, AssessmentResult, AssessmentSettings, Course, ExperienceType } from '@shared/models/course.model';
import { LoadState } from '@app/main/analytics/loader-directive/loader.module';
import { AssessmentService } from '@shared/services/assessment.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AssessmentEditDialogComponent } from '@app/main/apps/forms/assessment/assessment-edit-dialog/assessment-edit-dialog.component';
import { AssessmentReportDialogComponent } from './assessment-report-dialog/assessment-report-dialog.component';
import { AuthenticationService } from '@shared/services/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AssessmentGenerationConfirmationDialogComponent } from './assessment-generation-confirm-dialog/assessment-generation-confirm-dialog.component';
import { UserType } from '@shared/models';

const ASSESSMENTS_LIMIT = 20;
const MIN_AI_MSG_SENT = 5;
const EDITOR_FRAGMENT = 'editor';

@Component({
  selector: 'assessment',
  templateUrl: './assessment.component.html',
  styleUrls: ['./assessment.component.scss']
})

export class AssessmentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() course: Course;
  @Input() date_from: Date;
  @Input() date_to: Date;
  @Input() programsMode: boolean = false;
  @Input() previewMode: boolean = false;
  @Output() onExplore = new EventEmitter<{ sessionId: string, userName: string, course: Course }>();
  @Input() selectedUserType: UserType;
  @Output() onUserTypeChange = new EventEmitter<UserType>();

  public search: string;
  public sessions: SessionData[] = [];
  public assessments: AssessmentResult[] = [];
  public assessmentSettings: AssessmentSettings | undefined;
  public criteriaArray: [number, AssessmentCriteria][];
  public loadStatus: string;
  private fragmentSubscription?: Subscription;
  private isCreatingSubscription?: Subscription;
  private createdSubscription?: Subscription;

  constructor(
    private _assessmentService: AssessmentService,
    private _analyticsService: AnalyticsService,
    private _authenticationService: AuthenticationService,
    private _courseAPIService: CoursesAPIService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _dialog: MatDialog
  ) {
  }

  async ngOnInit() {
    this.fragmentSubscription = this._activatedRoute.fragment.subscribe(fragment => {
      if (fragment === EDITOR_FRAGMENT) {
        this.openAssessmentEditDialog();
      }
    });
    
    await this.prepareAssessments();
  }
  
  ngOnDestroy(): void {
    this.fragmentSubscription?.unsubscribe();
    this.isCreatingSubscription?.unsubscribe();
    this.createdSubscription?.unsubscribe();
  }
  
  async ngOnChanges(changes: SimpleChanges) {
    const fromChanged = changes.date_from && !changes.date_from.isFirstChange() && changes.date_from.currentValue !== changes.date_from.previousValue;
    const toChanged = changes.date_to && !changes.date_to.isFirstChange() && changes.date_to.currentValue !== changes.date_to.previousValue;
    const searchChanged = changes.search && changes.search.isFirstChange();
    const courseChanged = changes.course && !changes.course.isFirstChange() && changes.course.currentValue.id !== changes.course.previousValue.id;
    if (fromChanged || toChanged || searchChanged || courseChanged) {
      await this.prepareAssessments();
    }
  }
  
  private async prepareAssessments() {
    this.isCreatingSubscription?.unsubscribe();
    this.createdSubscription?.unsubscribe();

    this.updateAssessmentSettings();

    this.isCreatingSubscription = this._assessmentService.isCreating$(this.course.id).subscribe((isCreating: boolean) => {
      if (isCreating) {
        this.loadStatus = LoadState.loading;
      }
    });
    
    this.createdSubscription = this._assessmentService.created$(this.course.id).subscribe(() => {
      this.loadAssessments();
    });
    
    // We display a loader if report generation is in progress.
    // Otherwise, we load the existing reports.
    if (this._assessmentService.isCreating(this.course.id)) {
      this.loadStatus = LoadState.loading;
    } else {
      await this.loadAssessments();
    }
  }

  editAssessment() {
    if (this.course.experience_type === ExperienceType.ai) {
      // Edit assessment in the simplified editor.
      const url = `${this.course.learnspace.hub.urlTitle}/${this.course.learnspace_id}/simulations/${this.course.id}`;
      this._router.navigate([url], { queryParams: { step: 5 } });
    } else {
      // Edit assessment in the legacy dialog.
      this._router.navigate([], {
        fragment: EDITOR_FRAGMENT,
        queryParamsHandling: 'preserve'
      });
    }
  }

  private openAssessmentEditDialog() {
    if (this.course.experience_type === ExperienceType.ai) {
      // Assessment should be edited in the simplified editor for these experiences.
      return;
    }

    const dialog = this._dialog.open(AssessmentEditDialogComponent, {
      width: 'min(64vw, 1200px)',
      height: 'min(90vh, 900px)',
      panelClass: 'learnspace-theme',
      data: { course: this.course },
      autoFocus: false
    });
    dialog.afterClosed().subscribe(updatedCourse => {
      this.updateAssessmentSettings();
      this._router.navigate([], {
        fragment: null,
        queryParamsHandling: 'preserve'
      });
    });
  }

  private updateAssessmentSettings() {
    this.assessmentSettings = this.course.information?.assessment;
    this.criteriaArray = this.assessmentSettings?.criterias?.map((c, index) => [index, c]) || [];
  }

  async searchUser(userName: string) {
    this.search = userName;
    await this.loadAssessments();
  }

  async handleUserTypeChange(value: UserType) {
    this.onUserTypeChange.emit(value);
    await this.loadAssessments();
  }

  async loadAssessments() {
    this.loadStatus = LoadState.loading;
    try {
      const assessments = await this._assessmentService.getAssessments(this.course.id, ASSESSMENTS_LIMIT, this.search, this.selectedUserType).toPromise();
      const sessionIds = assessments.map(a => a.session_id);
      const queryOptions = { userName: this.search, limit: ASSESSMENTS_LIMIT, minAiMsgSent: MIN_AI_MSG_SENT };
      const sessions = await this._analyticsService.getSessionsByIds(sessionIds, queryOptions).toPromise();
      assessments.forEach(assessment => {
        assessment.session = sessions.find(s => s._id === assessment.session_id);
      });

      this.updateData(assessments, sessions as SessionData[]);
      this.loadStatus = LoadState.completed;
    } catch {
      this.loadStatus = LoadState.error;
    }
  }

  private updateData(assessments: AssessmentResult[], sessions: SessionData[]) {
    // Associate the assessment with it's corresponding session
    assessments.forEach(assessment => {
      assessment.session = sessions.find(s => s._id === assessment.session_id);
    });
    // Remove assessments for which we could not find the associated session
    assessments = assessments.filter(a => a.session);
    // Sort assessments by session date (most recent first)
    assessments.sort((a, b) => compareDatesDescendingOrder(a.session.date, b.session.date));

    // Get medians
    const settingsCriterias = this.course.information?.assessment?.criterias || [];
    settingsCriterias.forEach(settingsCriteria => {
      const criterias = assessments
        .map(assessment => {
          return assessment.report.criterias.find(criteria => criteria.title === settingsCriteria.title);
        })
        .filter(c => !!c && !isNaN(c.grade));
      const grades = criterias.map(c => c.grade);
      const median = calculateMedian(grades);
      criterias.forEach(c => c.medianGrade = median);
    });

    this.assessments = assessments;
    this.sessions = sessions;
  }

  confirmAssessmentsRegeneration(): void {
    this._dialog
      .open(AssessmentGenerationConfirmationDialogComponent, {
        width: 'min(600px, 90%)',
        height: 'auto',
        panelClass: ['learnspace-theme'],
        data: {
          programsMode: this.programsMode,
          defaultUserType: this.selectedUserType,
          hasExportButton: !this.previewMode,
        }
      })
      .afterClosed()
      .pipe(filter(val => val !== undefined))
      .subscribe(({ userType }) =>
        this.generateAssessments(userType)
      );
  }

  async generateAssessments(userType: string) {
    this.loadStatus = LoadState.loading;
    this.assessments = [];
    
    try {
      const params: any = {
        courseId: this.course.id,
        creatorId: this._authenticationService.wondaUser.id,
        trigger: 'analytics-view',
        limit: ASSESSMENTS_LIMIT,
        minAiMsgSent: MIN_AI_MSG_SENT,
        userType: userType
      };

      await this._assessmentService.generate(params).toPromise();
    } catch {
      this.loadStatus = LoadState.error;
    }
  }

  showAssessment(assessment: AssessmentResult) {
    const dialog = this._dialog.open(AssessmentReportDialogComponent, {
      width: 'min(80vw, 1200px)',
      height: 'min(90vh, 800px)',
      panelClass: ['learnspace-theme', 'assessment-report-dialog'],
      data: { assessment, course: this.course },
      autoFocus: false
    });
    dialog.componentInstance.onExplore = this.onExplore;
  }

  exportAssessments() {
    this._assessmentService
      .getAssessmentsExportDataByCourseId(this.course.id);
  }

  openHelp(): void {
    window.open('https://help.spaces.wondavr.com/en/articles/9728097-ai-assessment');
  }

  toggleLearnerReport() {
    this.assessmentSettings.learnerReportEnabled = !this.assessmentSettings.learnerReportEnabled;
    const information = {
      ...this.course.information,
      assessment: { ...this.assessmentSettings }
    };

    this._courseAPIService.updateCourse(this.course.id, { information }).subscribe(c => {
      this.assessmentSettings = c.information?.assessment;
    });
  }

  hasSessions(): boolean {
    return this.sessions && this.sessions.length > 0;
  }

  hasAssessments(): boolean {
    if (this.search) {
      // We might have no assessments to display if we search with an non-existing user.
      // In that case, we still want to display the view as if there are assessments
      return true
    };

    return this.assessments && this.assessments.length > 0;
  }
}

const calculateMedian = (grades: number[]): number => {
  const sortedGrades = [...grades].sort((a, b) => a - b);
  const mid = Math.floor(sortedGrades.length / 2)

  let median: number;
  if (sortedGrades.length % 2 !== 0) {
    median = sortedGrades[mid];
  } else {
    median = (sortedGrades[mid - 1] + sortedGrades[mid]) / 2;
  }

  return median;
}

const compareDatesDescendingOrder = (a: Date | string, b: Date | string): number => {
  const aDate = a instanceof Date ? a : new Date(a);
  const bDate = b instanceof Date ? b : new Date(b);
  return bDate.getTime() - aDate.getTime();
}
